<template>
  <azcon-calendar-card-skeleton v-if="loading" :events-position="eventsPosition"/>

  <div
    v-else
    class="azcon-calendar-card"
    :class="{
      'azcon-calendar-card--events-right': eventsPosition === 'right',
      'azcon-calendar-card--events-bottom': eventsPosition !== 'right',
    }"
  >
    <div class="azcon-calendar-card__body">
      <!-- Calendar surface -->
      <div class="azcon-calendar-card__surface">
      <!-- Month header -->
      <div class="azcon-calendar-card__header">
        <button class="azcon-calendar-card__nav" type="button" @click="prevMonth">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 13L7 9l4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="azcon-calendar-card__header-center">
          <span class="azcon-calendar-card__month">{{ monthLabel }}</span>
          <button
            v-if="!isCurrentMonth"
            class="azcon-calendar-card__today-btn"
            type="button"
            @click="goToToday"
          >{{ $t('general.today') || 'Bugün' }}</button>
        </div>
        <button class="azcon-calendar-card__nav" type="button" @click="nextMonth">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M7 5l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>

      <!-- Weekday labels -->
      <div class="azcon-calendar-card__weekdays">
        <span v-for="day in weekdays" :key="day" class="azcon-calendar-card__weekday">{{ day }}</span>
      </div>

      <!-- Day grid -->
      <div class="azcon-calendar-card__grid">
        <button
          v-for="(cell, i) in calendarCells"
          :key="i"
          type="button"
          class="azcon-calendar-card__cell"
          :class="{
            'azcon-calendar-card__cell--other': !cell.current,
            'azcon-calendar-card__cell--today': cell.isToday,
            'azcon-calendar-card__cell--selected': cell.isSelected,
            'azcon-calendar-card__cell--has-event': cell.hasEvent,
            'azcon-calendar-card__cell--weekend': cell.isWeekend,
          }"
          @click="selectDay(cell)"
        >
          <span class="azcon-calendar-card__day">{{ cell.day }}</span>
          <span v-if="cell.eventCount" class="azcon-calendar-card__dots">
            <span class="azcon-calendar-card__dot"></span>
            <span v-if="cell.eventCount >= 2" class="azcon-calendar-card__dot azcon-calendar-card__dot--alt"></span>
            <span v-if="cell.eventCount >= 3" class="azcon-calendar-card__dot azcon-calendar-card__dot--warn"></span>
          </span>
        </button>
      </div>

      <!-- Month summary -->
      <div class="azcon-calendar-card__summary">
        <span class="azcon-calendar-card__summary-item">
          <span class="azcon-calendar-card__summary-dot azcon-calendar-card__summary-dot--primary"></span>
          {{ monthEventCount }} {{ $t('general.record') }}
        </span>
      </div>
      </div>

      <!-- Selected day events -->
      <div class="azcon-calendar-card__events" :class="{ 'azcon-calendar-card__events--active': selectedDate && selectedDayItems.length }">
        <template v-if="selectedDate">
          <div class="azcon-calendar-card__events-header">
            <div class="azcon-calendar-card__events-header-left">
              <span class="azcon-calendar-card__events-date">{{ selectedDateLabel }}</span>
              <span v-if="selectedDayItems.length" class="azcon-calendar-card__events-count">{{ selectedDayItems.length }}</span>
            </div>
          </div>

          <div v-if="selectedDayItems.length" class="azcon-calendar-card__events-list">
            <article
              v-for="item in selectedDayItems"
              :key="item.type + '_' + item.id"
              class="azcon-calendar-card__event"
              @click="$emit('item-click', item)"
            >
              <span class="azcon-calendar-card__event-indicator" :class="'azcon-calendar-card__event-indicator--' + item.type"></span>
              <div class="azcon-calendar-card__event-body">
                <span class="azcon-calendar-card__event-title">{{ stripHtml(item.title) }}</span>
                <span class="azcon-calendar-card__event-time">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/><path d="M6 3.5V6l2 1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ formatEventDate(item.startDate) }} — {{ formatEventDate(item.endDate) }}
                </span>
              </div>
              <svg class="azcon-calendar-card__event-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </article>
          </div>

          <div v-else class="azcon-calendar-card__events-empty">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect x="4" y="6" width="24" height="22" rx="3" stroke="#cbd5e1" stroke-width="1.5"/>
              <path d="M4 12h24" stroke="#cbd5e1" stroke-width="1.5"/>
              <path d="M10 4v4M22 4v4" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="16" cy="20" r="2" fill="#e2e8f0"/>
            </svg>
            <span>{{ $t('general.no_records_today') }}</span>
          </div>
        </template>

        <div v-else class="azcon-calendar-card__events-placeholder">
          <span>{{ $t('general.select_day_to_see_records') }}</span>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="azcon-calendar-card__actions">
      <router-link :to="{ name: 'user.my-assignment' }" class="azcon-calendar-card__link">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
        {{ $t('general.view_all_assignments') || 'Tüm atamaları görüntüle' }}
      </router-link>
      <router-link :to="{ name: 'user.agenda' }" class="azcon-calendar-card__go">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2.5" width="11" height="10" rx="2" stroke="currentColor" stroke-width="1.2"/><path d="M1.5 5.5h11" stroke="currentColor" stroke-width="1.2"/><path d="M4.5 1v2M9.5 1v2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
        {{ $t('general.go_to_calendar') || 'Takvime git' }}
      </router-link>
    </div>
  </div>
</template>

<script>
import AzconCalendarCardSkeleton from "./AzconCalendarCardSkeleton.vue";
import moment from "moment";

export default {
  name: "AzconCalendarCard",
  components: { AzconCalendarCardSkeleton },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    eventsPosition: {
      type: String,
      default: "bottom",
      validator: (v) => ["bottom", "right"].includes(v),
    },
  },
  data() {
    const now = new Date();
    return {
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth(),
      selectedDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    };
  },
  computed: {
    weekdays() {
      const wd = moment.localeData().weekdaysMin();
      // moment weekdaysMin starts from Sunday, shift to Monday-first
      return [...wd.slice(1), wd[0]];
    },
    monthLabel() {
      return moment({ year: this.currentYear, month: this.currentMonth }).format('MMMM YYYY');
    },
    isCurrentMonth() {
      const now = new Date();
      return this.currentYear === now.getFullYear() && this.currentMonth === now.getMonth();
    },
    selectedDateLabel() {
      if (!this.selectedDate) return '';
      const m = moment(this.selectedDate);
      const today = new Date();
      if (this.dateKey(this.selectedDate) === this.dateKey(today)) {
        return (this.$t('general.today') || 'Bugün') + ', ' + m.format('D MMMM');
      }
      return m.format('D MMMM YYYY');
    },
    eventDateMap() {
      const map = {};
      this.items.forEach(item => {
        const start = item.startDate instanceof Date ? item.startDate : new Date(item.startDate);
        const end = item.endDate instanceof Date ? item.endDate : new Date(item.endDate);
        const d = new Date(start);
        while (d <= end) {
          const key = this.dateKey(d);
          map[key] = (map[key] || 0) + 1;
          d.setDate(d.getDate() + 1);
        }
      });
      return map;
    },
    monthEventCount() {
      let count = 0;
      Object.keys(this.eventDateMap).forEach(key => {
        const [y, m] = key.split('-').map(Number);
        if (y === this.currentYear && m === this.currentMonth + 1) {
          count += this.eventDateMap[key];
        }
      });
      return count;
    },
    calendarCells() {
      const cells = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const today = new Date();
      const todayKey = this.dateKey(today);
      const selectedKey = this.selectedDate ? this.dateKey(this.selectedDate) : null;

      let startDow = (firstDay.getDay() + 6) % 7;

      const prevLast = new Date(this.currentYear, this.currentMonth, 0);
      for (let i = startDow - 1; i >= 0; i--) {
        const day = prevLast.getDate() - i;
        const d = new Date(this.currentYear, this.currentMonth - 1, day);
        cells.push(this.makeCell(d, day, false, todayKey, selectedKey));
      }

      for (let day = 1; day <= lastDay.getDate(); day++) {
        const d = new Date(this.currentYear, this.currentMonth, day);
        cells.push(this.makeCell(d, day, true, todayKey, selectedKey));
      }

      const remaining = 7 - (cells.length % 7);
      if (remaining < 7) {
        for (let day = 1; day <= remaining; day++) {
          const d = new Date(this.currentYear, this.currentMonth + 1, day);
          cells.push(this.makeCell(d, day, false, todayKey, selectedKey));
        }
      }

      return cells;
    },
    selectedDayItems() {
      if (!this.selectedDate) return [];
      const key = this.dateKey(this.selectedDate);
      return this.items.filter(item => {
        const start = item.startDate instanceof Date ? item.startDate : new Date(item.startDate);
        const end = item.endDate instanceof Date ? item.endDate : new Date(item.endDate);
        const d = new Date(start);
        while (d <= end) {
          if (this.dateKey(d) === key) return true;
          d.setDate(d.getDate() + 1);
        }
        return false;
      });
    },
  },
  methods: {
    dateKey(d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    makeCell(date, day, current, todayKey, selectedKey) {
      const key = this.dateKey(date);
      const dow = (date.getDay() + 6) % 7;
      return {
        day,
        date,
        current,
        isToday: key === todayKey,
        isSelected: key === selectedKey,
        hasEvent: !!this.eventDateMap[key],
        eventCount: this.eventDateMap[key] || 0,
        isWeekend: dow >= 5,
      };
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.clampSelectedDate();
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.clampSelectedDate();
    },
    clampSelectedDate() {
      const prevDay = this.selectedDate ? this.selectedDate.getDate() : new Date().getDate();
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      this.selectedDate = new Date(this.currentYear, this.currentMonth, Math.min(prevDay, lastDay));
    },
    goToToday() {
      const now = new Date();
      this.currentYear = now.getFullYear();
      this.currentMonth = now.getMonth();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    },
    selectDay(cell) {
      this.selectedDate = cell.date;
      if (!cell.current) {
        this.currentYear = cell.date.getFullYear();
        this.currentMonth = cell.date.getMonth();
      }
    },
    formatEventDate(date) {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString(this.$i18n?.locale || 'tr', {
        day: 'numeric',
        month: 'short',
      });
    },
    stripHtml(str) {
      if (!str) return '';
      return str.replace(/<[^>]*>/g, '');
    },
  },
};
</script>

<style lang="scss" scoped>
.azcon-calendar-card {
  &__body {
    display: block;
  }

  &--events-right {
    .azcon-calendar-card__body {
      display: grid;
      grid-template-columns: minmax(0, 50%) minmax(0, 50%);
      gap: 14px;
      align-items: stretch;
    }

    .azcon-calendar-card__surface,
    .azcon-calendar-card__events {
      min-width: 0; // allow grid children to shrink, prevent overflow-x
    }

    .azcon-calendar-card__surface {
      height: 100%;
    }

    .azcon-calendar-card__events {
      height: 100%;
    }

    .azcon-calendar-card__events {
      margin-top: 0;
    }

    .azcon-calendar-card__events-list {
      max-height: 320px;
    }
  }

  &--events-bottom {
    .azcon-calendar-card__body {
      display: block;
    }
  }

  @media (max-width: 960px) {
    &--events-right {
      .azcon-calendar-card__body {
        grid-template-columns: 1fr;
      }

      .azcon-calendar-card__events {
        margin-top: 14px;
      }
    }
  }

  // ── Calendar surface ──────────────────────────────────────────────────
  &__surface {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  }

  // ── Header ────────────────────────────────────────────────────────────
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__header-center {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__month {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.3px;
  }

  &__today-btn {
    border: none;
    background: #eff6ff;
    color: #2563eb;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background: #dbeafe;
    }
  }

  &__nav {
    width: 34px;
    height: 34px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    color: #475569;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.18s;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #0f172a;
    }

    &:active {
      transform: scale(0.94);
    }
  }

  // ── Weekdays ──────────────────────────────────────────────────────────
  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__weekday {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }

  // ── Grid ──────────────────────────────────────────────────────────────
  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 4px 0;
  }

  // ── Cell ──────────────────────────────────────────────────────────────
  &__cell {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 42px;
    border: 2px solid transparent;
    border-radius: 10px;
    background: transparent;
    cursor: pointer;
    transition: all 0.18s;
    padding: 0;

    &:hover:not(&--selected) {
      background: #f8fafc;
      border-color: #e2e8f0;
    }

    &--weekend:not(&--other) {
      .azcon-calendar-card__day {
        color: #94a3b8;
      }
    }

    &--other {
      opacity: 0.35;
    }

    &--today:not(&--selected) {
      background: #eff6ff;
      border-color: #bfdbfe;

      .azcon-calendar-card__day {
        color: #1d4ed8;
        font-weight: 800;
      }
    }

    &--selected {
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
      border-color: transparent;
      box-shadow:
        0 4px 12px rgba(37, 99, 235, 0.25),
        0 1px 3px rgba(37, 99, 235, 0.15);

      .azcon-calendar-card__day {
        color: #fff;
        font-weight: 700;
      }

      .azcon-calendar-card__dot,
      .azcon-calendar-card__dot--alt,
      .azcon-calendar-card__dot--warn {
        background: rgba(255, 255, 255, 0.9);
        box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.3);
      }

      &:hover {
        background: linear-gradient(135deg, #1d4ed8, #1e40af);
      }
    }

    &--has-event:not(&--selected):not(&--other) {
      background: #fafbff;
    }
  }

  &__day {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1;
  }

  // ── Dots ──────────────────────────────────────────────────────────────
  &__dots {
    display: flex;
    gap: 3px;
    position: absolute;
    bottom: 4px;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #2563eb;
    transition: all 0.18s;

    &--alt {
      background: #7c3aed;
    }

    &--warn {
      background: #f59e0b;
    }
  }

  // ── Summary ───────────────────────────────────────────────────────────
  &__summary {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 10px;
    margin-top: 6px;
    border-top: 1px solid #f1f5f9;
  }

  &__summary-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
  }

  &__summary-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &--primary { background: #2563eb; }
  }

  // ── Events panel ──────────────────────────────────────────────────────
  &__events {
    margin-top: 14px;
    padding: 14px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
    overflow-x: hidden;

    &--active {
      background: #fff;
      border-color: #dbeafe;
      box-shadow: 0 1px 4px rgba(37, 99, 235, 0.06);
    }
  }

  &__events-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__events-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__events-date {
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.2px;
  }

  &__events-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 11px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
  }

  &__events-badge {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 3px 10px;
    border-radius: 8px;
  }

  &__events-list {
    display: grid;
    gap: 8px;
    max-height: calc(3 * 60px); // ~4 event item height
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 2px;
    }
  }

  // ── Event item ────────────────────────────────────────────────────────
  &__event {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    cursor: pointer;
    transition: all 0.2s;
    max-width: 100%;
    overflow: hidden;

    &:hover {
      border-color: #bfdbfe;
      box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
      .azcon-calendar-card__event-indicator {
        height: 32px;
        background: linear-gradient(180deg, #2563eb, #3b82f6);
      }
    }
  }

  &__event-indicator {
    width: 4px;
    height: 28px;
    border-radius: 2px;
    background: #2563eb;
    flex-shrink: 0;
    transition: all 0.2s;

    &--notice {
      background: #ec4899;
    }

    &--training {
      background: #0ea5e9;
    }
  }

  &__event-body {
    flex: 1;
    min-width: 0;
  }

  &__event-title {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__event-time {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    margin-top: 3px;

    svg {
      flex-shrink: 0;
    }
  }

  &__event-arrow {
    color: #cbd5e1;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  &__event:hover &__event-arrow {
    color: #2563eb;
  }

  // ── Placeholder (no date selected) ──────────────────────────────────
  &__events-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 0;

    span {
      font-size: 13px;
      color: #94a3b8;
      font-weight: 500;
    }
  }

  // ── Empty state ───────────────────────────────────────────────────────
  &__events-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 12px 0 4px;

    svg {
      opacity: 0.6;
    }

    span {
      font-size: 13px;
      color: #94a3b8;
      font-weight: 500;
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────
  &__actions {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #64748b;
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.18s;
    padding: 4px 0;

    &:hover {
      color: #2563eb;
      gap: 8px;
    }
  }

  &__go {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 10px;
    padding: 9px 16px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #fff !important;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none !important;
    transition: all 0.2s;
    box-shadow:
      0 1px 3px rgba(37, 99, 235, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &:hover {
      background: linear-gradient(135deg, #1d4ed8, #1e40af);
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}


</style>
